import { __assign } from 'tslib';
import React, { forwardRef, useState, useRef, useMemo } from 'react';
import cn from 'classnames';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import { hooks } from '../../../shared/esm';
import { useLayoutEffect_SAFE_FOR_SSR, useDidUpdateEffect } from '@alfalab/hooks';
import { DaysTable } from '../components/days-table/Component.js';
import { Header } from '../components/header/Component.js';
import { MonthYearHeader } from '../components/month-year-header/Component.js';
import { MonthsTable } from '../components/months-table/Component.js';
import { PeriodSlider } from '../components/period-slider/Component.js';
import 'date-fns/addDays';
import 'date-fns/addMonths';
import 'date-fns/addQuarters';
import 'date-fns/addWeeks';
import 'date-fns/addYears';
import 'date-fns/endOfMonth';
import 'date-fns/endOfQuarter';
import 'date-fns/endOfWeek';
import 'date-fns/endOfYear';
import 'date-fns/getQuarter';
import 'date-fns/getYear';
import 'date-fns/isToday';
import 'date-fns/isYesterday';
import 'date-fns/startOfQuarter';
import 'date-fns/startOfWeek';
import 'date-fns/startOfYear';
import { limitDate } from '../utils.js';
import { YearsTable } from '../components/years-table/Component.js';
import { useCalendar } from '../useCalendar.js';
import 'react-transition-group';
import 'date-fns/isEqual';
import 'date-fns/isLastDayOfMonth';
import 'date-fns/isSameDay';
import 'date-fns/isWithinInterval';
import '../../../button/esm/desktop';
import '@alfalab/icons-glyph/ChevronDownCompactSIcon';
import '../components/select-button/Component.js';
import 'date-fns/isSameMonth';
import 'date-fns/isThisMonth';
import '../../../icon-button/esm';
import '@alfalab/icons-glyph/ChevronBackMIcon';
import 'date-fns/eachDayOfInterval';
import 'date-fns/eachMonthOfInterval';
import 'date-fns/eachYearOfInterval';
import 'date-fns/format';
import 'date-fns/isAfter';
import 'date-fns/isBefore';
import 'date-fns/lastDayOfMonth';
import 'date-fns/max';
import 'date-fns/min';
import 'date-fns/parse';
import 'date-fns/subDays';
import 'date-fns/subMonths';
import 'date-fns/isSameYear';
import 'date-fns/isThisYear';
import 'react-merge-refs';
import 'date-fns/setYear';
import 'date-fns/subYears';
import '../components/period-slider/utils.js';

var styles = {"component":"calendar__component_1jp53","responsive":"calendar__responsive_1jp53","period":"calendar__period_1jp53","monthYear":"calendar__monthYear_1jp53","container":"calendar__container_1jp53","customScrollbar":"calendar__customScrollbar_1jp53","nativeScrollbar":"calendar__nativeScrollbar_1jp53","sixWeeks":"calendar__sixWeeks_1jp53"};
require('./desktop.css');

var useCustomWebkitScrollbar = hooks.useCustomWebkitScrollbar;
var CalendarDesktop = forwardRef(function (_a, ref) {
    var _b, _c;
    var className = _a.className, headerClassName = _a.headerClassName, contentClassName = _a.contentClassName, _d = _a.defaultView, defaultView = _d === void 0 ? 'days' : _d, _e = _a.selectorView, selectorView = _e === void 0 ? 'full' : _e, value = _a.value, monthTimestamp = _a.month, minDateTimestamp = _a.minDate, maxDateTimestamp = _a.maxDate, _f = _a.defaultMonth, defaultMonthTimestamp = _f === void 0 ? +new Date() : _f, selectedFrom = _a.selectedFrom, selectedTo = _a.selectedTo, rangeComplete = _a.rangeComplete, offDays = _a.offDays, events = _a.events, holidays = _a.holidays, onChange = _a.onChange, onMonthChange = _a.onMonthChange, onMonthClick = _a.onMonthClick, onYearClick = _a.onYearClick, onPeriodClick = _a.onPeriodClick, dataTestId = _a.dataTestId, _g = _a.hasHeader, hasHeader = _g === void 0 ? true : _g, responsive = _a.responsive, dayAddons = _a.dayAddons, _h = _a.shape, shape = _h === void 0 ? 'rounded' : _h, _j = _a.showCurrentYearSelector, showCurrentYearSelector = _j === void 0 ? false : _j;
    var _k = useState(defaultView), view = _k[0], setView = _k[1];
    var _l = useState(false), scrolled = _l[0], setScrolled = _l[1];
    var scrollableNodeRef = useRef(null);
    var firstUpdate = useRef(true);
    var selected = useMemo(function () { return (value ? new Date(value) : undefined); }, [value]);
    var defaultMonth = useMemo(function () {
        return startOfMonth(selected ||
            limitDate(defaultMonthTimestamp, minDateTimestamp, maxDateTimestamp));
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    var month = useMemo(function () { return (monthTimestamp ? new Date(monthTimestamp) : undefined); }, [monthTimestamp]);
    var minDate = useMemo(function () { return (minDateTimestamp ? startOfDay(minDateTimestamp) : undefined); }, [minDateTimestamp]);
    var maxDate = useMemo(function () { return (maxDateTimestamp ? endOfDay(maxDateTimestamp) : undefined); }, [maxDateTimestamp]);
    var _m = useCalendar({
        month: month,
        defaultMonth: defaultMonth,
        view: view,
        minDate: minDate,
        maxDate: maxDate,
        selected: selected,
        offDays: offDays,
        events: events,
        holidays: holidays,
        dayAddons: dayAddons,
        onChange: onChange,
        onMonthChange: onMonthChange,
    }), activeMonth = _m.activeMonth, weeks = _m.weeks, months = _m.months, years = _m.years, canSetPrevMonth = _m.canSetPrevMonth, canSetNextMonth = _m.canSetNextMonth, setMonthByDate = _m.setMonthByDate, setPrevMonth = _m.setPrevMonth, setNextMonth = _m.setNextMonth, highlighted = _m.highlighted, getDayProps = _m.getDayProps, getMonthProps = _m.getMonthProps, getYearProps = _m.getYearProps, getRootProps = _m.getRootProps;
    var toggleView = function (newView) {
        if (scrollableNodeRef.current)
            scrollableNodeRef.current.scrollTop = 0;
        setView(view === newView ? 'days' : newView);
    };
    var handleScroll = function (event) {
        return setScrolled(event.currentTarget.scrollTop > 0);
    };
    var handlePrevArrowClick = function () {
        // TODO: Что должны делать стрелки при view !== days?
        setPrevMonth();
    };
    var handleNextArrowClick = function () { return setNextMonth(); };
    var handleMonthClick = function (event) {
        toggleView('months');
        if (onMonthClick) {
            onMonthClick(event);
        }
    };
    var handleYearClick = function (event) {
        toggleView('years');
        if (onYearClick) {
            onYearClick(event);
        }
    };
    useLayoutEffect_SAFE_FOR_SSR(function () {
        if (view === 'years') {
            var listNode = scrollableNodeRef.current;
            var selectedYearNode = listNode === null || listNode === void 0 ? void 0 : listNode.querySelector('button[tabIndex="0"]');
            if (listNode && selectedYearNode) {
                var topIndent = listNode.clientHeight / 2 - selectedYearNode.clientHeight / 2;
                listNode.scrollTop = selectedYearNode.offsetTop - topIndent;
                setScrolled(listNode.scrollTop > 0);
            }
        }
    }, [view]);
    useLayoutEffect_SAFE_FOR_SSR(function () {
        if (firstUpdate.current) {
            firstUpdate.current = false;
        }
        else {
            toggleView('days');
        }
    }, [activeMonth]);
    useDidUpdateEffect(function () {
        setScrolled(false);
    }, [view]);
    useDidUpdateEffect(function () {
        var newMonth = value && startOfMonth(value);
        if (newMonth && newMonth.getTime() !== activeMonth.getTime()) {
            setMonthByDate(newMonth);
        }
    }, [value]);
    var shouldUseCustomScrollbar = useCustomWebkitScrollbar();
    return (React.createElement("div", __assign({}, getRootProps({ ref: ref }), { className: cn('cc-calendar', styles.component, className, (_b = {},
            _b[styles.sixWeeks] = weeks.length === 6,
            _b[styles.responsive] = responsive,
            _b)), "data-test-id": dataTestId }),
        hasHeader && (React.createElement(Header, { view: selectorView, withShadow: scrolled, className: headerClassName }, selectorView === 'month-only' ? (React.createElement(PeriodSlider, { className: styles.period, value: activeMonth, periodType: 'month', prevArrowDisabled: !canSetPrevMonth, nextArrowDisabled: !canSetNextMonth, hideDisabledArrows: true, showCurrentYearSelector: showCurrentYearSelector, onPrevArrowClick: handlePrevArrowClick, onNextArrowClick: handleNextArrowClick, onPeriodClick: onPeriodClick })) : (React.createElement(MonthYearHeader, { className: styles.monthYear, value: activeMonth, onMonthClick: handleMonthClick, onYearClick: handleYearClick })))),
        React.createElement("div", { ref: scrollableNodeRef, className: cn(styles.container, (_c = {},
                _c[styles.customScrollbar] = view === 'years' && shouldUseCustomScrollbar,
                _c[styles.nativeScrollbar] = view === 'years' && !shouldUseCustomScrollbar,
                _c), styles[view], contentClassName), onScroll: handleScroll },
            view === 'days' && (React.createElement(DaysTable, { weeks: weeks, activeMonth: activeMonth, selectedFrom: selectedFrom, selectedTo: selectedTo, getDayProps: getDayProps, highlighted: highlighted, rangeComplete: rangeComplete, responsive: responsive, shape: shape, withTransition: selectorView === 'month-only' })),
            view === 'months' && (React.createElement(MonthsTable, { selectedMonth: activeMonth, months: months, getMonthProps: getMonthProps, responsive: responsive })),
            view === 'years' && (React.createElement(YearsTable, { selectedYear: activeMonth, years: years, getYearProps: getYearProps, responsive: responsive })))));
});

export { CalendarDesktop };
